import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { ISLAND_BY_SLUG } from "../../lib/constants";

/**
 * Traffic Sources — acquisition breakdown for the admin Overview tab.
 *
 * Reads from `GET /api/admin/analytics/traffic-sources` and shows:
 *   - Bucket cards (Direct / Organic / Social / Referral / Internal)
 *   - Ranked top referring domains per bucket
 *   - 7d / 30d window toggle
 *   - Island filter dropdown (All / Aruba / Curaçao / Bonaire / Sint Maarten)
 *   - WhatsApp share callout (ties into the digest / sharing work)
 */

const BUCKET_META = {
  direct:   { label: "Direct",   icon: "🔗", tone: "#0AADA0",
              desc: "Typed URL, bookmarks, app opens, dark social" },
  organic:  { label: "Organic",  icon: "🔎", tone: "#22C55E",
              desc: "Search engines — Google, Bing, DuckDuckGo…" },
  social:   { label: "Social",   icon: "📣", tone: "#F59E0B",
              desc: "Facebook, Instagram, WhatsApp, X, TikTok…" },
  referral: { label: "Referral", icon: "🌐", tone: "#6366F1",
              desc: "Other external sites linking to us" },
  internal: { label: "Internal", icon: "↩️", tone: "#94A3B8",
              desc: "Navigation within IslandsDaily" },
};

const BUCKET_ORDER = ["direct", "organic", "social", "referral", "internal"];

const DomainRow = ({ domain, count, max, tone }) => (
  <li className="flex items-center gap-3 text-sm">
    <span className="text-[#1A1A1A] w-40 flex-shrink-0 truncate" title={domain}>
      {domain === "direct" ? "— direct —" : domain}
    </span>
    <div className="flex-1 h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden">
      <div
        className="h-full rounded-full"
        style={{ width: `${(count / max) * 100}%`, backgroundColor: tone }}
      />
    </div>
    <span className="text-xs text-[#6B7280] w-10 text-right font-mono">
      {count}
    </span>
  </li>
);

const BucketCard = ({ bucket, count, pct, domains }) => {
  const meta = BUCKET_META[bucket] || BUCKET_META.referral;
  const max = Math.max(1, ...(domains || []).map((d) => d.count));
  return (
    <div
      className="card-soft p-5"
      data-testid={`traffic-bucket-${bucket}`}
    >
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg leading-none">{meta.icon}</span>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280]">
              {meta.label}
            </div>
            <div className="text-[10px] text-[#94A3B8] mt-0.5">
              {meta.desc}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div
            className="font-serif-display text-3xl"
            style={{ color: meta.tone }}
            data-testid={`traffic-bucket-${bucket}-pct`}
          >
            {pct.toFixed(1)}%
          </div>
          <div className="text-[11px] text-[#6B7280] font-mono">
            {count.toLocaleString()} views
          </div>
        </div>
      </div>
      {(domains || []).length === 0 ? (
        <p className="text-xs text-[#94A3B8] italic">No traffic in this bucket.</p>
      ) : (
        <ul className="space-y-1.5 mt-3 pt-3 border-t border-[#F1F5F9]">
          {domains.slice(0, 5).map((d) => (
            <DomainRow
              key={d.domain}
              domain={d.domain}
              count={d.count}
              max={max}
              tone={meta.tone}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

const EMPTY = { total: 0, buckets: [], top_domains_by_bucket: {},
                whatsapp_share: { count: 0, pct: 0 } };

export const TrafficSourcesCard = () => {
  const [days, setDays] = useState(30);
  const [island, setIsland] = useState("all");
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    api
      .get("/admin/analytics/traffic-sources", {
        params: { days, island },
      })
      .then((r) => { if (!cancelled) setData(r.data || EMPTY); })
      .catch((e) => { if (!cancelled) setErr(e.message || "Failed to load"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days, island]);

  // Ensure every bucket appears in the grid — even those with 0 views —
  // so the operator can see at a glance which channels are dormant.
  const bucketsFull = useMemo(() => {
    const byBucket = Object.fromEntries(
      (data.buckets || []).map((b) => [b.bucket, b])
    );
    return BUCKET_ORDER.map((b) => byBucket[b] || {
      bucket: b, count: 0, pct: 0,
    });
  }, [data.buckets]);

  const islands = Object.values(ISLAND_BY_SLUG || {});

  return (
    <div className="space-y-4" data-testid="traffic-sources-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-[13px] uppercase tracking-wider text-[#1A1A1A] font-medium flex items-center gap-2">
            <span>Traffic sources</span>
            {loading && (
              <span className="text-[10px] text-[#94A3B8] normal-case">
                loading…
              </span>
            )}
          </h2>
          <p className="text-xs text-[#6B7280] mt-1">
            Where your {data.total?.toLocaleString?.() ?? 0} pageviews came
            from over the last {days} days
            {island !== "all" && ISLAND_BY_SLUG[island]
              ? ` · ${ISLAND_BY_SLUG[island].flag} ${ISLAND_BY_SLUG[island].name}`
              : ""}.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md bg-[#F1F5F9] p-0.5"
               data-testid="traffic-window-toggle">
            {[7, 30].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={
                  "px-3 py-1 text-xs rounded transition " +
                  (days === d
                    ? "bg-white text-[#0AADA0] shadow-sm font-medium"
                    : "text-[#6B7280] hover:text-[#1A1A1A]")
                }
                data-testid={`traffic-window-${d}d`}
              >
                {d}d
              </button>
            ))}
          </div>

          <select
            value={island}
            onChange={(e) => setIsland(e.target.value)}
            className="text-xs bg-white border border-[#E5E5E5] rounded-md px-2 py-1.5 text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#0AADA0]/30"
            data-testid="traffic-island-filter"
          >
            <option value="all">All islands</option>
            {islands.map((i) => (
              <option key={i.slug} value={i.slug}>
                {i.flag} {i.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {err ? (
        <div className="card-soft p-5 text-sm text-red-600">
          Couldn&apos;t load traffic sources: {err}
        </div>
      ) : data.total === 0 ? (
        <div className="card-soft p-5 text-sm text-[#6B7280]"
             data-testid="traffic-empty">
          No pageviews recorded in this window yet.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {bucketsFull.map((b) => (
              <BucketCard
                key={b.bucket}
                bucket={b.bucket}
                count={b.count}
                pct={b.pct}
                domains={data.top_domains_by_bucket?.[b.bucket] || []}
              />
            ))}
          </div>

          {data.whatsapp_share?.count > 0 && (
            <div
              className="card-soft p-4 flex items-center gap-4"
              data-testid="traffic-whatsapp-callout"
            >
              <div className="text-2xl leading-none">💬</div>
              <div className="flex-1">
                <div className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                  WhatsApp share
                </div>
                <div className="text-sm text-[#1A1A1A] mt-0.5">
                  <span className="font-serif-display text-xl text-[#25D366] mr-2">
                    {data.whatsapp_share.pct.toFixed(1)}%
                  </span>
                  of pageviews arrived from WhatsApp
                  <span className="text-[#6B7280] ml-1">
                    ({data.whatsapp_share.count.toLocaleString()} views)
                  </span>
                </div>
              </div>
            </div>
          )}

          {data.island_coverage && island !== "all" && (
            <p
              className="text-xs text-[#94A3B8] italic"
              data-testid="traffic-coverage-note"
            >
              {data.island_coverage.note} Filtered coverage:{" "}
              {data.island_coverage.pageviews_with_island.toLocaleString()} of{" "}
              {data.island_coverage.pageviews_total_window.toLocaleString()}{" "}
              pageviews in this window carry an island tag.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default TrafficSourcesCard;
